import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Container, Button, Row, Col, Modal, Form, Badge, Dropdown } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import moment from "moment";
import { Box } from "@mui/material";
import { DataGrid, GridToolbar, gridClasses } from "@mui/x-data-grid";
import contactApi from "../../services/contactapi";
import $ from "jquery";
import "bootstrap-datepicker";
import DynamicBreadcrumb from "../header/breadcrumb";
import { showConfirm } from "react-confirm-prompt";
import "../../static/css/App.css";
import "../../static/css/customerTable.css";

const AllContacts = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [totalCustomer, setTotalCustomer] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 15 });
  const [sortModel, setSortModel] = useState([{ field: "id", sort: "desc" }]);
  const [filterModel, setFilterModel] = useState({ items: [] });

  const [enquiryTypeFilter, setEnquiryTypeFilter] = useState(null);
  const [contactTypeFilter, setContactTypeFilter] = useState(null); // NEW: Form Type filter
  const [createdDateFilter, setCreatedDateFilter] = useState({ start: null, end: null });

  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyMessage, setReplyMessage] = useState("");
  const [replyMessageError, setReplyMessageError] = useState("");

  const pathArray = useMemo(() => [{ name: "Contactus Enquiry List", url: null }], []);

  const enquiryTypes = useMemo(
    () => ({
      1: "General Enquiry",
      2: "Product Enquiry",
      3: "Complaint",
      4: "Feedback",
      5: "Others",
    }),
    []
  );

  const enquiryFroms = useMemo(() => ({ 1: "Contact", 2: "Feedback" }), []);
  const statusArr = useMemo(
    () => ({
      0: "Pending",
      1: "Replied",
      2: "Ignored",
      3: "Deleted",
      4: "Blocked / Spam",
    }),
    []
  );

  const statusColors = useMemo(
    () => ({
      Pending: "secondary",
      Replied: "success",
      Ignored: "danger",
      Deleted: "dark",
      "Blocked / Spam": "warning",
    }),
    []
  );

//   console.log(contactTypeFilter,"contactTypeFilter");

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const { page, pageSize } = paginationModel;
      const data = await contactApi.getAllContacts({
        limit: pageSize,
        page,
        sortModel,
        // Pass explicit filters to API (includes new contactType)
        filterModel: {
          ...filterModel,
          enquiryType: enquiryTypeFilter,
          contactType: contactTypeFilter, // NEW
          createdDate: createdDateFilter,
        },
      });

      const formattedRows = data.data.result.map((cust, index) => ({
        id: cust.id,
        sno: index + 1,
        createdAt: moment(cust.createdAt).format("YYYY/MM/DD HH:mm:ss"),
        customer_details: `${cust.name} - ${cust.mobile} - ${cust.email}`,
        location: cust.location,
        enquiryType: enquiryTypes[cust.enquiryType] || "Others",
        contactType: enquiryFroms[cust.contactType] || "Others",
        message: cust.message,
        status: statusArr[cust.status] || "Pending",
        reply: cust.reply,
      }));

      setRows(formattedRows);
      setTotalCustomer(data.data.totalRecords[0]?.total_records || 0);
    } catch (error) {
      console.error("Failed to fetch Contacts:", error);
    } finally {
      setLoading(false);
    }
  }, [paginationModel, sortModel, filterModel, enquiryTypeFilter, contactTypeFilter, createdDateFilter]); // include new deps

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const columns = useMemo(
    () => [
      { field: "sno", headerName: "S NO", sortable: false },
      { field: "createdAt", headerName: "Created ON", flex: 1 },
      { field: "customer_details", headerName: "Customer Details", flex: 1 },
      {
        field: "message",
        headerName: "Content",
        flex: 1,
        renderCell: ({ value }) => (
          <span title={value}>{value.length > 110 ? `${value.substring(0, 150)}...` : value}</span>
        ),
      },
      { field: "enquiryType", headerName: "Enquiry Type", flex: 1 },
      { field: "contactType", headerName: "Enquiry Form Type", flex: 1 },
      {
        field: "status",
        headerName: "Status",
        flex: 1,
        renderCell: ({ value }) => <Badge pill bg={statusColors[value] || "secondary"}>{value}</Badge>,
      },
      {
        field: "action",
        headerName: "Action",
        sortable: false,
        renderCell: ({ row }) => <Button variant="primary" onClick={() => openViewModal(row)}>View</Button>,
      },
    ],
    []
  );

  const openViewModal = (row) => {
    setSelectedMessage(row);
    setShowViewModal(true);
  };

  const handleCloseModal = () => {
    setShowViewModal(false);
    setReplyMessage("");
    setReplyMessageError("");
  };

  const handleReplySubmit = () => {
    if (!replyMessage.trim()) {
      setReplyMessageError("Please enter a reply message.");
      return;
    }
    setReplyMessageError("");
    handleStatusSubmit("Replied", replyMessage);
  };

  const handleStatusChange = (status) => {
    const confirmText =
      {
        Ignored: {
          title: "Are you sure you want to ignore this enquiry?",
          desc: "This will mark the enquiry as ignored.",
        },
        Blocked: {
          title: "Are you sure you want to block this enquiry?",
          desc: "This will mark the enquiry as blocked.",
        },
      }[status] || { title: "Confirm", desc: "" };

    setShowViewModal(false);

    showConfirm(confirmText.title, {
      description: confirmText.desc,
      type: "info",
    }).then((answer) => {
      if (answer) {
        handleStatusSubmit(status);
      } else {
        setShowViewModal(true);
      }
    });
  };

  const handleStatusSubmit = async (status, reason = null) => {
    try {
      const statusMap = { Ignored: 2, Replied: 1, Blocked: 4 };
      const response = await contactApi.updateContactStatus({
        id: selectedMessage.id,
        status: statusMap[status],
        reply: reason,
      });

      const message = response?.message || "Contact status updated successfully";
      showConfirm("Success", {
        description: message,
        type: "success",
        hideCancel: true,
      }).then(() => {
        handleCloseModal();
        fetchContacts();
      });
    } catch (error) {
      console.error("Failed to update status:", error);
    }
  };

  const applyFilters = () => {
    // Keep DataGrid-aware filter model too (for server use if you need it),
    // include new contactType and existing enquiryType + date range.
    setFilterModel({
      items: [
        createdDateFilter.start && { columnField: "createdAt", operatorValue: "isAfter", value: createdDateFilter.start },
        createdDateFilter.end && { columnField: "createdAt", operatorValue: "isBefore", value: createdDateFilter.end },
        enquiryTypeFilter != null && { columnField: "enquiryType", operatorValue: "equals", value: enquiryTypeFilter },
        contactTypeFilter != null && { columnField: "contactType", operatorValue: "equals", value: contactTypeFilter }, // NEW
      ].filter(Boolean),
    });
  };

  const resetFilters = () => {
    setEnquiryTypeFilter(null);
    setContactTypeFilter(null); // NEW
    setCreatedDateFilter({ start: null, end: null });
    setFilterModel({ items: [] });
    $("#startDate").datepicker("clearDates");
    $("#endDate").datepicker("clearDates");
  };

  useEffect(() => {
    $("#startDate")
      .datepicker({
        format: "yyyy-mm-dd",
        clearBtn: true,
        autoclose: true,
        todayHighlight: true,
        endDate: "today",
      })
      .on("changeDate", (e) => {
        setCreatedDateFilter((prev) => ({ ...prev, start: e.format("yyyy-mm-dd") }));
        $("#endDate").datepicker("setStartDate", e.date);
      });

    $("#endDate")
      .datepicker({
        format: "yyyy-mm-dd",
        clearBtn: true,
        autoclose: true,
        todayHighlight: true,
        endDate: "today",
      })
      .on("changeDate", (e) => {
        setCreatedDateFilter((prev) => ({ ...prev, end: e.format("yyyy-mm-dd") }));
      });

    return () => {
      $("#startDate").datepicker("destroy");
      $("#endDate").datepicker("destroy");
    };
  }, []);

  return (
    <Container>
      <DynamicBreadcrumb breadcrumb={pathArray} />
      <h5>All Enquiries</h5>

      <Row className="mb-4">
        {/* Enquiry Type (Feedback & Others hidden) */}
        <Col sm={3} className="mb-2">
          <Dropdown>
            <Dropdown.Toggle variant="success" id="dropdown-enquiry-type">
              Enquiry Type: {enquiryTypes[enquiryTypeFilter] || "All"}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => setEnquiryTypeFilter(null)}>All</Dropdown.Item>
              {Object.entries(enquiryTypes)
                .filter(([key]) => key !== "4" && key !== "5") // HIDE Feedback (4) & Others (5)
                .map(([key, label]) => (
                  <Dropdown.Item key={key} onClick={() => setEnquiryTypeFilter(Number(key))}>
                    {label}
                  </Dropdown.Item>
                ))}
            </Dropdown.Menu>
          </Dropdown>
        </Col>

        {/* NEW: Form Type (Contact / Feedback) */}
        <Col sm={3} className="mb-2">
          <Dropdown>
            <Dropdown.Toggle variant="info" id="dropdown-form-type">
              Form Type: {enquiryFroms[contactTypeFilter] || "All"}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => setContactTypeFilter(null)}>All</Dropdown.Item>
              {Object.entries(enquiryFroms).map(([key, label]) => (
                <Dropdown.Item key={key} onClick={() => setContactTypeFilter(Number(key))}>
                  {label}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </Col>

        <Col sm={2} className="mb-2">
          <input id="startDate" type="text" className="form-control" placeholder="Start Date" />
        </Col>
        <Col sm={2} className="mb-2">
          <input id="endDate" type="text" className="form-control" placeholder="End Date" />
        </Col>

        <Col sm={2} className="d-flex justify-content-between mb-2">
          <Button variant="primary" onClick={applyFilters}>Apply Filters</Button>
          <Button variant="secondary" onClick={resetFilters} className="ms-2">
            Reset
          </Button>
        </Col>
      </Row>

      <Box>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowHeight={() => "auto"}
          rowsPerPageOptions={[5, 10, 20]}
          paginationMode="server"
          rowCount={totalCustomer}
          loading={loading}
          checkboxSelection
          disableRowSelectionOnClick
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortingMode="server"
          filterMode="server"
          onSortModelChange={setSortModel}
          onFilterModelChange={setFilterModel}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              printOptions: { disableToolbarButton: true },
              csvOptions: { disableToolbarButton: true },
            },
          }}
          onRowSelectionModelChange={setRowSelectionModel}
          rowSelectionModel={rowSelectionModel}
          sx={{ [`& .${gridClasses.cell}`]: { py: 0.5 } }}
        />
      </Box>

      <Modal size="lg" show={showViewModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>View Message</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p><strong>Customer:</strong> {selectedMessage?.customer_details}</p>
          <p><strong>Enquiry Type:</strong> {selectedMessage?.enquiryType}</p>
          <p><strong>Message:</strong> {selectedMessage?.message}</p>
          {selectedMessage?.reply && <p><strong>Reply:</strong> {selectedMessage.reply}</p>}
          {selectedMessage?.status === "Pending" && (
            <Form.Group className="mb-3">
              <Form.Label>Reply Message</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                style={{
                  borderColor: replyMessageError ? "red" : "",
                  boxShadow: replyMessageError ? "0 0 5px red" : "",
                }}
              />
              {replyMessageError && <Form.Text className="text-danger">{replyMessageError}</Form.Text>}
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Col sm={12} className="d-flex justify-content-between">
            <Button variant="secondary" onClick={handleCloseModal}>Close</Button>
            {selectedMessage?.status === "Pending" && (
              <>
                <Button variant="primary" onClick={handleReplySubmit}>Send Reply</Button>
                <Button variant="danger" onClick={() => handleStatusChange("Ignored")}>Ignore</Button>
                <Button variant="warning" onClick={() => handleStatusChange("Blocked")}>Block</Button>
              </>
            )}
          </Col>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AllContacts;
